/**
 * Display dates using the logged-in user's `display_date_format` preference.
 *
 * **Source of truth:** preferences are read from the decoded JWT via `getUser()` — they are
 * embedded in the access token at login and are not loaded with a separate API call. After the
 * user updates preferences in User Management they must sign in again (or otherwise receive a
 * new token) for date formatting to reflect the change.
 *
 * Use `formatDisplayDate` / `formatDisplayDateTime` (or alias `formatDate`) everywhere in the UI.
 */
import dayjs from 'dayjs';
import type { GridColDef } from '@mui/x-data-grid';
import { getUser } from '../services/auth';

export const DISPLAY_DATE_FORMAT_PREF_KEY = 'display_date_format';

/** Used when the user has no `display_date_format` in the JWT, or it is empty / not one of the allowed values. */
export const DEFAULT_DISPLAY_DATE_FORMAT = 'YYYY-MM-DD';

/** Maps backend `display_date_format` values to dayjs format tokens. Anything else falls back to `YYYY-MM-DD`. */
export function preferenceToDayjsFormat(preference: string | undefined | null): string {
  const p = (preference || '').trim();
  if (p === 'DD-MM-YYYY' || p === 'MM-DD-YYYY' || p === 'YYYY-MM-DD') {
    return p;
  }
  return DEFAULT_DISPLAY_DATE_FORMAT;
}

/**
 * Resolves `display_date_format` from JWT claims (`getUser()` / jwtDecode), not from HTTP APIs.
 * If the preference is missing, blank, or unknown, returns **`YYYY-MM-DD`** (`DEFAULT_DISPLAY_DATE_FORMAT`).
 */
export function getDisplayDateFormatPreference(): string {
  try {
    const u = getUser();
    const raw = u?.preferences?.[DISPLAY_DATE_FORMAT_PREF_KEY];
    return preferenceToDayjsFormat(typeof raw === 'string' ? raw : null);
  } catch {
    return DEFAULT_DISPLAY_DATE_FORMAT;
  }
}

/** Calendar date only (user preference). */
export function formatDisplayDate(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const d = dayjs(value);
  if (!d.isValid()) return String(value);
  return d.format(getDisplayDateFormatPreference());
}

/** Date + time (24h); date portion uses user preference. */
export function formatDisplayDateTime(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const d = dayjs(value);
  if (!d.isValid()) return String(value);
  const df = getDisplayDateFormatPreference();
  return d.format(`${df} HH:mm:ss`);
}

/** @deprecated Prefer formatDisplayDate — kept for existing imports */
export const formatDate = formatDisplayDate;

export const toISODateString = (date: Date | null): string => {
  if (!date) return '';

  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error converting date to ISO string:', error);
    return '';
  }
};

/**
 * Attach valueFormatter to DataGrid columns for known date/datetime fields.
 * Skips columns that already define valueFormatter or renderCell.
 */
export function withGridDateColumns(columns: GridColDef[]): GridColDef[] {
  return columns.map((col) => {
    if (col.valueFormatter != null || col.renderCell != null) {
      return col;
    }
    const field = col.field;
    if (field === 'last_loaded_date') {
      return {
        ...col,
        valueFormatter: (value: unknown) => {
          if (value == null || value === '') return '';
          return formatDisplayDateTime(String(value));
        },
      } as GridColDef;
    }
    const isDateField =
      field === 'metric_date' ||
      field === 'due_date' ||
      field === 'fulfillment_date' ||
      (typeof field === 'string' && field.endsWith('_date'));
    if (isDateField) {
      return {
        ...col,
        valueFormatter: (value: unknown) => {
          if (value == null || value === '') return '';
          return formatDisplayDate(String(value));
        },
      } as GridColDef;
    }
    return col;
  });
}

/**
 * Validates that date relationships follow required rules
 */
export const validateDateRelationships = (dates: {
  baselineStartDate?: string | null;
  baselineEndDate?: string | null;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
}): { isValid: boolean; errorMessage: string } => {
  const { baselineStartDate, baselineEndDate, actualStartDate, actualEndDate } = dates;

  const baselineStart = baselineStartDate ? new Date(baselineStartDate) : null;
  const baselineEnd = baselineEndDate ? new Date(baselineEndDate) : null;
  const actualStart = actualStartDate ? new Date(actualStartDate) : null;
  const actualEnd = actualEndDate ? new Date(actualEndDate) : null;

  if (baselineStart && baselineEnd && baselineEnd < baselineStart) {
    return {
      isValid: false,
      errorMessage: 'Baseline End Date must be greater than or equal to Baseline Start Date',
    };
  }

  if (actualStart && actualEnd && actualEnd < actualStart) {
    return {
      isValid: false,
      errorMessage: 'Actual End Date must be greater than or equal to Actual Start Date',
    };
  }

  if (baselineStart && actualStart && actualStart < baselineStart) {
    return {
      isValid: false,
      errorMessage: 'Actual Start Date must be greater than or equal to Baseline Start Date',
    };
  }

  return { isValid: true, errorMessage: '' };
};
